import { BellRing } from "lucide-react";
import type { FC } from "react";

import { MetaLine } from "@/components/ui/meta-line";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useCoverage, useLibrarySearch } from "../hooks/useResearch";
import { RESEARCH_SOURCES } from "../sources";
import type { LibraryHit } from "../types";

const number = new Intl.NumberFormat("en-US");

const refreshed = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : `Refreshed ${date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      })}`;
};

type Props = {
  onSearch: (query: string) => void;
  onOpen: (hit: LibraryHit) => void;
  activeId: string | null;
};

/** Before a search: what the library holds, what to try, and what just changed. */
export const ResearchLanding: FC<Props> = ({ onSearch, onOpen, activeId }) => {
  const coverage = useCoverage();
  const updates = useLibrarySearch("updates", "", { limit: 6 });
  const c = coverage.data;
  const stats = c
    ? [
        [c.cases, "Decisions"],
        [c.statutes, "Statute Sections"],
        [c.regulations, "C.F.R. Parts"],
        [c.references, "Manual Chapters"],
        [c.updates, "Policy Updates"]
      ]
    : [];
  return (
    <div
      className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-6"
      data-testid="research-landing"
    >
      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-base font-medium text-fg">In the Library</h2>
          <span className="text-sm text-fg-3">{refreshed(c?.updated_at)}</span>
        </div>
        {coverage.isPending ? (
          <Skeleton className="h-16 w-full rounded-xl" />
        ) : stats.length ? (
          <div className="grid grid-cols-5 divide-x divide-line-1 rounded-xl ring-1 ring-line-2">
            {stats.map(([n, label]) => (
              <div key={label} className="flex flex-col gap-0.5 px-3 py-3">
                <span className="text-lg font-medium text-fg tabular-nums">
                  {number.format(Number(n))}
                </span>
                <span className="text-sm text-fg-3">{label}</span>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-base font-medium text-fg">Try a Search</h2>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          {RESEARCH_SOURCES.filter((s) => !s.browsable).map((source) => (
            <div key={source.id} className="flex flex-col gap-1">
              <span className="text-sm text-fg-3">{source.label}</span>
              {source.examples.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => onSearch(example)}
                  className="-mx-1.5 truncate rounded-md px-1.5 py-1 text-left text-base text-fg-2 hover:bg-fill-1 hover:text-fg"
                >
                  {example}
                </button>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="flex items-center gap-1.5 text-base font-medium text-fg">
          <BellRing className="size-3.5 text-fg-3" />
          Latest Policy Updates
        </h2>
        {updates.isPending ? (
          <Skeleton className="h-32 w-full rounded-xl" />
        ) : (
          <ul className="divide-y divide-line-1 rounded-xl ring-1 ring-line-2">
            {(updates.data?.results ?? []).map((hit, i) => (
              <li key={hit.id ?? i}>
                <button
                  type="button"
                  disabled={!hit.id}
                  onClick={() => onOpen(hit)}
                  className={cn(
                    "flex w-full flex-col gap-0.5 px-3 py-2.5 text-left first:rounded-t-xl last:rounded-b-xl hover:bg-fill-1",
                    hit.id && hit.id === activeId && "bg-fill-3"
                  )}
                >
                  <span className="truncate text-base text-fg">
                    {hit.title}
                  </span>
                  <MetaLine parts={[hit.citation, hit.date]} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
